import React, { useState, useEffect } from 'react';
import {
  Show,
  useRecordContext,
  useRedirect,
  useDataProvider,
  useNotify,
} from 'react-admin';
import ResourceBreadcrumbsWithActions from '../../components/ResourceBreadcrumbsWithActions';
import { FranchisesTable } from '../../components/FranchisesTable';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Avatar,
  Divider,
  Tabs,
  Tab,
  Container,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Store as StoreIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { EnterpriseMetrics } from '../../types/franchise';
import { formatCurrency, formatPercentage, getComplianceStatus } from '../../types/franchise';
import { FranchiseDetailDialog } from '../../components/FranchiseDetailDialog';

const EnterpriseSummaryCard = () => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar
              src={record.logoUrl}
              sx={{
                width: 80,
                height: 80,
                bgcolor: '#1976d2',
                fontSize: '1.5rem',
                fontWeight: 'bold',
              }}
            >
              <BusinessIcon sx={{ fontSize: 40, color: 'white' }} />
            </Avatar>
          </Grid>

          <Grid item xs>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
              {record.name}
            </Typography>

            {record.description && (
              <Typography variant="body1" color="text.secondary">
                {record.description}
              </Typography>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Created: {new Date(record.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const MetricsCard = ({ metrics, error }: { metrics: EnterpriseMetrics | null; error?: string }) => {
  if (!metrics) {
    return (
      <Card>
        <CardContent>
          <Typography color={error ? "error" : "textSecondary"}>
            {error || "Loading metrics..."}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const complianceStatus = getComplianceStatus(metrics.summary.averageComplianceScore);

  return (
    <Grid container spacing={2}>
      {/* Summary Metrics */}
      <Grid item xs={12} md={3}>
        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <StoreIcon color="primary" />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {metrics.summary.totalBrands}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Brands
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={3}>
        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <LocationIcon color="secondary" />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {metrics.summary.totalRegions}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Regions
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={3}>
        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <PeopleIcon color="success" />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {metrics.summary.totalFranchisees}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Franchisees
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={3}>
        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <AssessmentIcon sx={{ color: complianceStatus.color }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {metrics.summary.averageComplianceScore !== null
              ? `${metrics.summary.averageComplianceScore.toFixed(1)}%`
              : 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Avg. Compliance
          </Typography>
        </Paper>
      </Grid>

      {/* Revenue Metrics */}
      <Grid item xs={12} md={4}>
        <Paper elevation={1} sx={{ p: 2, bgcolor: 'success.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <MoneyIcon sx={{ color: 'success.main', mr: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Total Revenue
            </Typography>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
            {formatCurrency(metrics.revenue.totalRevenue)}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper elevation={1} sx={{ p: 2, bgcolor: 'primary.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <MoneyIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Total Royalties
            </Typography>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {formatCurrency(metrics.revenue.totalRoyalties)}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TrendingUpIcon sx={{ color: 'info.main', mr: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Avg. Royalty Rate
            </Typography>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
            {formatPercentage(metrics.revenue.averageRoyaltyRate)}
          </Typography>
        </Paper>
      </Grid>

      {/* Invoice Metrics */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon color="primary" />
              Invoice Performance
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Invoices
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {metrics.invoices.total}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Paid Invoices
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {metrics.invoices.paid}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Invoice Revenue
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {formatCurrency(metrics.invoices.totalRevenue)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const BrandsTab = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const [brands, setBrands] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!record?.id) return;

    const fetchBrands = async () => {
      try {
        const response = await dataProvider.getList('brands', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: { enterpriseId: record.id },
        });
        setBrands(response.data);
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, [record?.id, dataProvider]);

  if (loading) {
    return <Typography>Loading brands...</Typography>;
  }

  if (brands.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No brands found for this enterprise
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Grid container spacing={2}>
      {brands.map((brand: any) => (
        <Grid item xs={12} md={6} key={brand.id}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 3,
              },
            }}
            onClick={() => redirect(`/brands/${brand.id}/show`)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <StoreIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {brand.name}
                  </Typography>
                  {brand.industry && (
                    <Chip label={brand.industry} size="small" sx={{ mt: 0.5 }} />
                  )}
                </Box>
              </Box>
              {brand.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {brand.description}
                </Typography>
              )}
              {brand._count && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Regions
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {brand._count.regions || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Franchisees
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {brand._count.tenants || 0}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

const FranchiseesTab = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [brands, setBrands] = useState<any[]>([]);
  const [regions, setRegions] = useState<any[]>([]);
  const [franchisees, setFranchisees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filteredLoading, setFilteredLoading] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [selectedRegionId, setSelectedRegionId] = useState<string>('');
  const [selectedFranchisee, setSelectedFranchisee] = useState<any | null>(null);

  // Fetch brands for enterprise
  useEffect(() => {
    if (!record?.id) return;

    const fetchBrands = async () => {
      try {
        setLoading(true);
        const brandsResult = await dataProvider.getList('brands', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: { enterpriseId: record.id },
        });
        setBrands(brandsResult.data || []);
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, [record?.id, dataProvider]);

  // Fetch regions scoped to selected brand
  useEffect(() => {
    if (!record?.id) {
      setRegions([]);
      return;
    }

    setSelectedRegionId('');

    const fetchRegions = async () => {
      try {
        const filter: any = { enterpriseId: record.id };
        if (selectedBrandId) {
          filter.brandId = selectedBrandId;
        }

        const regionsResult = await dataProvider.getList('regions', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter,
        });
        setRegions(regionsResult.data || []);
      } catch (error) {
        console.error('Error fetching regions:', error);
        setRegions([]);
      }
    };

    fetchRegions();
  }, [record?.id, selectedBrandId, dataProvider]);

  // Fetch franchisees with filters
  useEffect(() => {
    if (!record?.id) return;

    const fetchFranchisees = async () => {
      try {
        setFilteredLoading(true);
        const filter: any = { enterpriseId: record.id };

        if (selectedBrandId) {
          filter.brandId = selectedBrandId;
        }

        if (selectedRegionId) {
          filter.regionId = selectedRegionId;
        }

        const result = await dataProvider.getList('tenants', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'code', order: 'ASC' },
          filter,
        });

        setFranchisees(result.data || []);
      } catch (error) {
        console.error('Error fetching franchisees:', error);
        setFranchisees([]);
      } finally {
        setFilteredLoading(false);
      }
    };

    fetchFranchisees();
  }, [record?.id, selectedBrandId, selectedRegionId, dataProvider]);

  return (
    <Box>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Brand</InputLabel>
                <Select
                  value={selectedBrandId}
                  onChange={(e) => setSelectedBrandId(e.target.value)}
                  label="Brand"
                >
                  <MenuItem value="">All Brands</MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled={!selectedBrandId}>
                <InputLabel>Region</InputLabel>
                <Select
                  value={selectedRegionId}
                  onChange={(e) => setSelectedRegionId(e.target.value)}
                  label="Region"
                >
                  <MenuItem value="">
                    {selectedBrandId ? 'All Regions' : 'Select a brand first'}
                  </MenuItem>
                  {regions.map((region) => (
                    <MenuItem key={region.id} value={region.id}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Franchisees Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Franchises ({franchisees.length})
            </Typography>
            {filteredLoading && <CircularProgress size={24} />}
          </Box>
          <FranchisesTable
            franchisees={franchisees}
            loading={filteredLoading}
            showBrand={true}
            showRegion={true}
            onRowClick={(franchisee) => setSelectedFranchisee(franchisee)}
          />
        </CardContent>
      </Card>

      {/* Franchise Detail Dialog */}
      <FranchiseDetailDialog
        open={!!selectedFranchisee}
        franchisee={selectedFranchisee}
        onClose={() => setSelectedFranchisee(null)}
      />
    </Box>
  );
};

const EnterpriseShowContent = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [activeTab, setActiveTab] = useState(0);
  const [metrics, setMetrics] = useState<EnterpriseMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState<string | null>(null);

  useEffect(() => {
    if (!record?.id) return;

    const fetchMetrics = async () => {
      setMetricsError(null);
      try {
        const response = await dataProvider.getOne('franchise-analytics/enterprise', {
          id: record.id,
        });
        setMetrics(response.data as EnterpriseMetrics);
      } catch (error) {
        console.error('Error fetching metrics:', error);
        setMetricsError('Metrics not available');
        notify('Failed to load enterprise metrics', { type: 'warning' });
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, [record?.id, dataProvider, notify]);

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box>
        <EnterpriseSummaryCard />

        <Card sx={{ mt: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(event, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 2 }}
            >
              <Tab label="Overview" />
              <Tab label="Brands" />
              <Tab label="Franchises" />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {activeTab === 0 && (
              <Box>
                {metricsLoading ? (
                  <Typography>Loading metrics...</Typography>
                ) : (
                  <MetricsCard metrics={metrics} error={metricsError} />
                )}
              </Box>
            )}
            {activeTab === 1 && <BrandsTab />}
            {activeTab === 2 && <FranchiseesTab />}
          </Box>
        </Card>
      </Box>
    </Container>
  );
};

const EnterpriseTitle = () => {
  const record = useRecordContext();
  if (!record) return 'Loading...';
  return record.name || 'Enterprise';
};

export const EnterpriseShow = () => (
  <Show actions={false} title={<EnterpriseTitle />}>
    <ResourceBreadcrumbsWithActions hideEdit={false} hideDelete={false} />
    <EnterpriseShowContent />
  </Show>
);
